import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { X } from "lucide-react";
import type { PermissionCatalogItem } from "@/types/permission-catalog";
import { Role } from "@/types/user";
import {
  FRONTDESK_BLOCKS,
  FRONTDESK_PERMISSION_CATALOG,
  getFrontdeskAccessLevel,
  setFrontdeskAccessLevel,
  type FrontdeskAccessLevel,
} from "@/constants/frontdeskPermissions";
import type {
  CreateCustomRolePayload,
  CustomRoleScope,
} from "@/components/pages/users/hooks/useCustomRoles";
import {
  countPlatformPermissionSections,
  normalizePlatformRolePermissions,
  togglePlatformRolePermission,
} from "@/components/pages/users/utils/platformRolePermissions";

type TranslationFn = ReturnType<typeof useTranslation>["t"];

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;
const DEFAULT_ALLOWED_BASE_ROLES: readonly Role[] = [
  Role.TENANT_ADMIN,
  Role.FRONT_DESK,
];

interface AddCustomRoleModalProps {
  open: boolean;
  onClose: () => void;
  scope: CustomRoleScope;
  permissionCatalog: PermissionCatalogItem[];
  isPermissionCatalogLoading?: boolean;
  mode?: "create" | "edit";
  initialRole?: {
    name: string;
    description?: string | null;
    baseRole?: Role;
    permissionKeys: string[];
  };
  assigneeCount?: number;
  onSubmit: (payload: CreateCustomRolePayload) => Promise<void>;
  isSubmitting?: boolean;
  allowedBaseRoles?: readonly Role[];
}

function getPermissionTranslationKey(permissionKey: string): string {
  return permissionKey.replace(/:/g, ".");
}

function getDialogTitle(
  mode: "create" | "edit",
  scope: CustomRoleScope,
  t: TranslationFn,
): string {
  if (mode === "edit" && scope === "platform") {
    return t(
      "users.roles.modal.editPlatformTitle",
      "Edit Platform Custom Role",
    );
  }

  if (mode === "edit") {
    return t("users.roles.modal.editTitle", "Edit Custom Role");
  }

  if (scope === "platform") {
    return t("users.roles.modal.platformTitle", "Create Platform Custom Role");
  }

  return t("users.roles.modal.title");
}

function getDialogDescription(
  scope: CustomRoleScope,
  t: TranslationFn,
): string {
  if (scope === "platform") {
    return t(
      "users.roles.modal.platformDescription",
      "Platform roles grant Guesteria admins access to platform-scoped actions.",
    );
  }

  return t("users.roles.modal.description");
}

function getAssigneeImpactText(
  scope: CustomRoleScope,
  assigneeCount: number,
  t: TranslationFn,
): string {
  if (scope === "platform") {
    return t("users.roles.modal.platformAssigneeImpact", {
      defaultValue:
        "This role is assigned to {{count}} platform admin(s). Changes apply on their next request.",
      count: assigneeCount,
    });
  }

  return t("users.roles.modal.assigneeImpact", {
    defaultValue:
      "This role is assigned to {{count}} user(s). Saving will re-sync their permissions on next sign-in.",
    count: assigneeCount,
  });
}

function getPermissionRequiredText(
  scope: CustomRoleScope,
  t: TranslationFn,
): string {
  if (scope === "platform") {
    return t(
      "users.roles.modal.atLeastOnePlatformPermission",
      "A platform role must include at least one permission.",
    );
  }

  return t(
    "users.roles.modal.atLeastOnePermission",
    "A custom role must include at least one permission.",
  );
}

function getAvailablePermissions(
  scope: CustomRoleScope,
  baseRole: Role,
  permissionCatalog: PermissionCatalogItem[],
): PermissionCatalogItem[] {
  return scope === "tenant" && baseRole === Role.FRONT_DESK
    ? FRONTDESK_PERMISSION_CATALOG
    : permissionCatalog.filter(
        (permission) => !permission.resource.startsWith("frontdesk_"),
      );
}

function getSubmitLabel(mode: "create" | "edit", t: TranslationFn): string {
  if (mode === "edit") {
    return t("users.roles.modal.saveEdit", "Save");
  }

  return t("users.roles.modal.save");
}

function renderPermissionGroups({
  groupedPermissions,
  selectedPermissionKeys,
  togglePermission,
  t,
}: {
  groupedPermissions: Array<{
    category: string;
    permissions: PermissionCatalogItem[];
  }>;
  selectedPermissionKeys: string[];
  togglePermission: (permissionKey: string) => void;
  t: TranslationFn;
}): React.ReactNode {
  return groupedPermissions.map((group) => (
    <section key={group.category} className="flex flex-col gap-2">
      <h4 className="text-muted-foreground text-xs font-semibold tracking-normal uppercase">
        {t(
          `users.roles.permissions.categories.${group.category}`,
          group.category,
        )}
      </h4>
      <div className="flex flex-col gap-2">
        {group.permissions.map((permission) => {
          const checked = selectedPermissionKeys.includes(permission.key);
          const translationKey = getPermissionTranslationKey(permission.key);
          const checkboxId = `permission-${permission.key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

          return (
            <label
              htmlFor={checkboxId}
              key={permission.key}
              className="border-base-border hover:bg-secondary-badge flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
            >
              <Checkbox
                id={checkboxId}
                checked={checked}
                onCheckedChange={() => togglePermission(permission.key)}
                className="mt-0.5"
              />
              <span className="flex min-w-0 flex-col gap-1">
                <span className="text-foreground text-sm font-medium">
                  {t(
                    `users.roles.permissions.labels.${translationKey}`,
                    permission.label,
                  )}
                </span>
                <span className="text-muted-foreground font-mono text-xs">
                  {permission.key}
                </span>
                {permission.description ? (
                  <span className="text-muted-foreground text-xs leading-5">
                    {t(
                      `users.roles.permissions.descriptions.${translationKey}`,
                      permission.description,
                    )}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
    </section>
  ));
}

export const AddCustomRoleModal: React.FC<AddCustomRoleModalProps> = ({
  open,
  onClose,
  scope,
  permissionCatalog,
  isPermissionCatalogLoading = false,
  mode = "create",
  initialRole,
  assigneeCount = 0,
  onSubmit,
  isSubmitting = false,
  allowedBaseRoles = DEFAULT_ALLOWED_BASE_ROLES,
}) => {
  const { t } = useTranslation();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [selectedPermissionKeys, setSelectedPermissionKeys] = React.useState<
    string[]
  >([]);
  const [selectedBaseRole, setSelectedBaseRole] = React.useState<Role>(
    allowedBaseRoles[0] ?? Role.TENANT_ADMIN,
  );

  const availablePermissions = React.useMemo(
    () => getAvailablePermissions(scope, selectedBaseRole, permissionCatalog),
    [permissionCatalog, scope, selectedBaseRole],
  );

  const availablePermissionKeys = React.useMemo(
    () => new Set(availablePermissions.map((permission) => permission.key)),
    [availablePermissions],
  );

  // Runs once per modal-open (or initialRole change) to seed form state.
  // Deliberately excludes availablePermissions/availablePermissionKeys: those
  // are derived from selectedBaseRole, which this effect also sets — including
  // them here would re-fire the effect right after the user picks a different
  // base role and snap the selection back.
  React.useEffect(() => {
    if (!open) {
      return;
    }

    setName(initialRole?.name ?? "");
    setDescription(initialRole?.description ?? "");

    const nextBaseRole =
      initialRole?.baseRole ?? allowedBaseRoles[0] ?? Role.TENANT_ADMIN;
    setSelectedBaseRole(nextBaseRole);

    const initialKeys = initialRole?.permissionKeys ?? [];
    const openPermissions = getAvailablePermissions(
      scope,
      nextBaseRole,
      permissionCatalog,
    );
    if (openPermissions.length === 0) {
      setSelectedPermissionKeys(initialKeys);
      return;
    }

    const openPermissionKeys = new Set(
      openPermissions.map((permission) => permission.key),
    );
    const availableInitialKeys = initialKeys.filter((key) =>
      openPermissionKeys.has(key),
    );
    setSelectedPermissionKeys(
      scope === "platform"
        ? normalizePlatformRolePermissions(availableInitialKeys)
        : availableInitialKeys,
    );
  }, [allowedBaseRoles, initialRole, open, permissionCatalog, scope]);

  React.useEffect(() => {
    if (scope !== "tenant") {
      return;
    }

    setSelectedPermissionKeys((current) =>
      current.filter((key) => availablePermissionKeys.has(key)),
    );
  }, [availablePermissionKeys, scope]);

  const groupedPermissions = React.useMemo(() => {
    const groups = new Map<string, PermissionCatalogItem[]>();

    for (const permission of availablePermissions) {
      const category = permission.category || permission.resource;
      groups.set(category, [...(groups.get(category) ?? []), permission]);
    }

    return Array.from(groups.entries())
      .map(([category, permissions]) => ({
        category,
        permissions: [...permissions].sort((a, b) =>
          a.key.localeCompare(b.key),
        ),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [availablePermissions]);

  const trimmedName = name.trim();
  const isNameTooLong = trimmedName.length > NAME_MAX_LENGTH;
  const isDescriptionTooLong = description.length > DESCRIPTION_MAX_LENGTH;
  const isValid =
    trimmedName.length > 0 &&
    !isNameTooLong &&
    !isDescriptionTooLong &&
    selectedPermissionKeys.length > 0 &&
    !isPermissionCatalogLoading;

  const togglePermission = (permissionKey: string) => {
    setSelectedPermissionKeys((current) => {
      if (scope === "platform") {
        return togglePlatformRolePermission(current, permissionKey);
      }
      if (current.includes(permissionKey)) {
        return current.filter((key) => key !== permissionKey);
      }

      return [...current, permissionKey];
    });
  };

  const handleSubmit = async () => {
    if (!isValid) return;

    const payload: CreateCustomRolePayload = {
      name: trimmedName,
      description: description.trim() || undefined,
      permissionKeys:
        scope === "platform"
          ? normalizePlatformRolePermissions(selectedPermissionKeys)
          : selectedPermissionKeys,
    };

    if (scope === "tenant") {
      payload.baseRole = selectedBaseRole;
    }

    await onSubmit(payload);
  };

  const dialogTitle = getDialogTitle(mode, scope, t);
  const dialogDescription = getDialogDescription(scope, t);
  const assigneeImpactText = getAssigneeImpactText(scope, assigneeCount, t);
  const permissionRequiredText = getPermissionRequiredText(scope, t);
  const submitLabel = getSubmitLabel(mode, t);
  const permissionCountLabel =
    scope === "platform"
      ? t(
          "users.roles.modal.selectedAccessSections",
          "{{selected}} of {{total}} access sections selected",
          {
            selected: countPlatformPermissionSections(selectedPermissionKeys),
            total: countPlatformPermissionSections(
              availablePermissions.map((permission) => permission.key),
            ),
          },
        )
      : t(
          "users.roles.modal.selectedPermissions",
          "{{selected}} of {{total}} permissions selected",
          {
            selected: selectedPermissionKeys.length,
            total: availablePermissions.length,
          },
        );

  let permissionEditor: React.ReactNode;
  if (selectedBaseRole === Role.FRONT_DESK) {
    permissionEditor = (
      <div className="border-base-border overflow-hidden rounded-md border">
        {FRONTDESK_BLOCKS.map((block) => {
          const value = getFrontdeskAccessLevel(
            selectedPermissionKeys,
            block.resource,
          );

          return (
            <div
              key={block.resource}
              className="border-base-border grid grid-cols-1 items-start gap-3 border-b p-3 last:border-b-0 sm:grid-cols-[1fr_180px] sm:items-center"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {t(
                    `users.roles.frontdeskBlocks.${block.resource}.label`,
                    block.label,
                  )}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t(
                    `users.roles.frontdeskBlocks.${block.resource}.description`,
                    block.description,
                  )}
                </p>
              </div>
              <Select
                value={value}
                onValueChange={(nextValue: FrontdeskAccessLevel) =>
                  setSelectedPermissionKeys((current) =>
                    setFrontdeskAccessLevel(current, block.resource, nextValue),
                  )
                }
              >
                <SelectTrigger
                  size="sm"
                  className="w-full justify-between sm:w-[180px]"
                  aria-label={t(
                    "users.roles.modal.accessLevelFor",
                    "Access level for {{name}}",
                    { name: block.label },
                  )}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="none">
                      {t("users.roles.accessLevels.none", "None")}
                    </SelectItem>
                    <SelectItem value="read">
                      {t("users.roles.accessLevels.view", "View")}
                    </SelectItem>
                    <SelectItem value="administrate">
                      {t(
                        "users.roles.accessLevels.administrating",
                        "Administrating",
                      )}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    );
  } else if (isPermissionCatalogLoading) {
    permissionEditor = (
      <p className="border-base-border rounded-md border p-3 text-sm text-black/65">
        {t("common.loading", "Loading...")}
      </p>
    );
  } else {
    permissionEditor = (
      <div className="border-base-border flex max-h-[360px] flex-col gap-3 overflow-y-auto rounded-md border bg-white p-3">
        {renderPermissionGroups({
          groupedPermissions,
          selectedPermissionKeys,
          togglePermission,
          t,
        })}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogOverlay />
      <DialogContent
        showCloseButton={false}
        className="border-base-border flex max-h-[min(860px,calc(100vh-48px))] w-[600px] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-xl border bg-white p-0 shadow-lg sm:max-w-[600px]"
      >
        <DialogHeader className="border-base-border relative shrink-0 gap-1 border-b px-6 py-4 pr-14 text-left">
          <DialogTitle className="text-base leading-normal font-semibold text-black/96">
            {dialogTitle}
          </DialogTitle>
          <DialogDescription className="text-sm leading-normal whitespace-pre-line text-black/65">
            {dialogDescription}
          </DialogDescription>
          <DialogClose asChild>
            <Button
              type="button"
              variant="new_ghost"
              size="icon_sm"
              className="absolute top-3 right-4 rounded-md text-black/55"
              disabled={isSubmitting}
              aria-label={t("users.roles.modal.close", "Close dialog")}
            >
              <X />
            </Button>
          </DialogClose>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
            <Input
              id="custom-role-name"
              label={t("users.roles.modal.roleLabel")}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("users.roles.modal.rolePlaceholder")}
              maxLength={NAME_MAX_LENGTH}
              aria-invalid={isNameTooLong || undefined}
              autoComplete="off"
            />
            <div className="-mt-4 flex items-center justify-between text-xs text-black/55">
              <span>
                {isNameTooLong
                  ? t(
                      "users.roles.modal.nameTooLong",
                      "Name is too long (max {{max}})",
                      {
                        max: NAME_MAX_LENGTH,
                      },
                    )
                  : ""}
              </span>
              <span>
                {trimmedName.length}/{NAME_MAX_LENGTH}
              </span>
            </div>

            <Textarea
              id="custom-role-description"
              label={t("users.roles.modal.descriptionLabel", "Description")}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t(
                "users.roles.modal.descriptionPlaceholder",
                "Optional description",
              )}
              maxLength={DESCRIPTION_MAX_LENGTH}
              aria-invalid={isDescriptionTooLong || undefined}
              className="min-h-20"
            />
            <div className="-mt-4 flex items-center justify-between text-xs text-black/55">
              <span>
                {isDescriptionTooLong
                  ? t(
                      "users.roles.modal.descriptionTooLong",
                      "Description is too long (max {{max}})",
                      { max: DESCRIPTION_MAX_LENGTH },
                    )
                  : ""}
              </span>
              <span>
                {description.length}/{DESCRIPTION_MAX_LENGTH}
              </span>
            </div>

            {scope === "tenant" ? (
              <div className="flex flex-col gap-1.5">
                <Label
                  htmlFor="custom-role-base-role"
                  className="text-black/96"
                >
                  {t("users.roles.modal.baseRole", "Base role")}
                </Label>
                <Select
                  value={selectedBaseRole}
                  disabled={mode === "edit"}
                  onValueChange={(nextRole: Role) => {
                    setSelectedBaseRole(nextRole);
                    setSelectedPermissionKeys([]);
                  }}
                >
                  <SelectTrigger
                    id="custom-role-base-role"
                    className="w-full justify-between"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {allowedBaseRoles.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role === Role.FRONT_DESK
                            ? t("users.table.frontDesk", "Front Desk")
                            : t("users.table.tenantAdmin", "Tenant Admin")}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-xs leading-5 text-black/55">
                  {t(
                    "users.roles.modal.baseRoleDescription",
                    "The base role selects the application and the maximum available access.",
                  )}
                </p>
              </div>
            ) : null}

            {mode === "edit" && assigneeCount > 0 ? (
              <div className="border-base-border bg-secondary-badge rounded-md border px-3 py-2 text-xs text-black/65">
                {assigneeImpactText}
              </div>
            ) : null}

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm leading-none font-medium text-black/96">
                  {t("users.roles.modal.accessTitle")}
                </h3>
                <span className="text-xs text-black/55">
                  {permissionCountLabel}
                </span>
              </div>

              {permissionEditor}

              {selectedPermissionKeys.length === 0 ? (
                <p className="text-custom-destructive-dark text-xs">
                  {permissionRequiredText}
                </p>
              ) : null}
            </div>
          </div>

          <DialogFooter className="border-base-border shrink-0 flex-row justify-end gap-2 border-t px-6 py-4">
            <Button
              type="button"
              variant="new_outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("users.roles.modal.cancel")}
            </Button>
            <Button
              type="submit"
              variant="new_default"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting
                ? t("users.roles.modal.saving", "Saving...")
                : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
